import React, {FC} from 'react';
import Box from '@mui/material/Box';
import {VegaLite} from 'react-vega';
import {TopLevelSpec} from 'vega-lite';
import {DashboardData, TypeChartData, UuidOptionColor} from '@/domain/services/Clas';
import {useTranslation} from 'react-i18next';
import CustomLegend from '@/components/Charts/CustomLegend';

export type DataWithProtection = DashboardData & {
  protection: Array<TypeChartData>
}

export interface BarChartProps {
  data: DataWithProtection,
  labels: Array<UuidOptionColor>,
  width: number,
  title: string,
  legendColumns?: number,
  legendColumnWidth?: number,
  dataType: 'use' | 'crop' | 'protection'
}

const BarChart: FC<BarChartProps> = ({data, labels, width, title, legendColumns = 2, legendColumnWidth= 450, dataType}) => {

  const {t} = useTranslation();

  const subData: Array<TypeChartData> = data[dataType].sort((a, b) => b.totalAreaInHa - a.totalAreaInHa) || [];
  const hasData = subData.length > 0;
  
  const formattedData = (hasData
    ? subData
    : labels.map(l => ({
      uuid: l.uuid,
      totalAreaInHa: 0,
      color: l.color,
      name: l.name
    }))).map(d => {
    const rounded = Number(d.totalAreaInHa?.toFixed(2));
    const label = labels.find(l => l.uuid === d.uuid)?.name || d.uuid;
    return {
      ...d,
      totalAreaInHa: rounded,
      color: labels.find(l => l.uuid === d.uuid)?.color || '#ccc',
      name: label,
      labelWithValue: `${label} (${rounded})`
    };
  });

  const spec: TopLevelSpec  = {
    $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
    data: {
      values: formattedData
    },
    width: width,
    height: 200,
    title: {
      text: subData.length ? title.toUpperCase() : title.toUpperCase().concat(t(' (No hay datos)')),
      fontSize: 16,
      color: '#50A684',
      align: 'left',
      dy: -30,
      dx: -250
    },
    transform: [
      {
        lookup: 'uuid', // El campo en `data`
        from: {
          data: { values: labels }, // Fuente de los labels
          key: 'uuid', // Campo en `labels` que coincide con `type`
          fields: ['name', 'color'] // Campo que queremos agregar a los datos
        }
      }
    ],
    mark: {
      type: 'bar',
      cornerRadiusEnd: 4
    },
    config: {
      background: undefined,
      axis: {
        domain: false,
        labelFontSize: 12,
        labelColor: '#363636',
        labelPadding: 10
      },
      axisX: {
        grid: false,
        ticks: false,
        title: null,
        labels: false
      },
      axisY: {
        grid: true,
        ticks: false,
        /* eslint-disable */ // esto no se puede quitar, porque no acepta que le ponga comillas simples
        labelExpr: "datum.value + ' (Ha)'",
        title: null
      },
      legend: undefined
    },
    view: {
      stroke: null
    },
    encoding: {
      x: {
        field: 'uuid',
        type: 'nominal',
        sort: {field: 'totalAreaInHa', order: 'descending'}
      },
      y: {
        field: 'totalAreaInHa',
        type: 'quantitative'
      },
      color: {
        field: 'uuid',
        type: 'nominal',
        sort: {field: 'totalAreaInHa', order: 'descending'},
        scale: {
        // Ordenar el domain basado en los datos
        domain: formattedData
          .sort((a, b) => b.totalAreaInHa - a.totalAreaInHa)
          .map(d => d.uuid),
        range: formattedData
          .sort((a, b) => b.totalAreaInHa - a.totalAreaInHa)
          .map(d => labels.find(l => l.uuid === d.uuid)?.color || '#ccc')
      },
        legend: null,
      },
      tooltip: [
        {
          field: 'name',
          title: t(dataType),
          type: 'nominal',
        },
        {
          field: 'totalAreaInHa',
          title: 'Superficie (Ha)',
          type: 'quantitative'
        }
      ]
    }
  };

  return <Box my={2} sx={{display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', flexDirection: 'row', gap: 2}}>
    <VegaLite spec={spec} actions={false}/>
    <CustomLegend data={formattedData} labels={labels} columns={legendColumns} columnWidth={legendColumnWidth} />
  </Box>;
};

export default BarChart;